/**
 * Dwell times - ONE resolver for the whole board.
 * ----------------------------------------------
 * Before this file there were four stores and three different lookups:
 * routingEngine read load_stops.preset_dwell_minutes (30 on every one of the 9106
 * rows - it is the column default and nothing ever set it), liveEta read
 * load_stops.dwell_minutes (NULL on every row), and durationCalculation did an
 * ilike match against dwell_time_settings. So every plan used a flat 30 minutes
 * and no real visit ever fed back in.
 *
 * Now: dwell_time_settings is the source of truth for places and
 * sql/dwell_learning.sql keeps ai_adjusted_dwell_minutes up to date from real
 * geofence visits; the hub's global per-cargo-type stop times (app_settings
 * cargo_type_stop_times, read through the public.stop_time_settings() RPC) and
 * the hub's weight/size tiers cover the stops no place has learned yet.
 *
 * __PLAN_BASELINE__ (2026-09-29) resolution order for a stop:
 *   1. an explicit per-stop override a human set   (load_stops.dwell_minutes)
 *   2. the location's learned value                (ai_adjusted, >= MIN_SAMPLES)
 *      - real measured visits beat any estimate
 *   3. the GLOBAL cargo-type stop time             (hub Settings -> Job Duration,
 *      exact match on loads.commodity, same rule as the hub's quote)
 *   4. the size tier                               (__STOP_TIME_TABLE__: over the
 *      heavy weight -> heavy minutes, else the first footprint tier the load fits;
 *      only when the load has a weight or a dimension - no size data skips it)
 *   5. the location's configured default           (default_dwell_minutes)
 *   6. the type default for the stop kind          (yard / customer / fuel_station)
 *   7. the routing config default                  (30)
 * load_stops.preset_dwell_minutes is only honoured when it differs from 30, i.e.
 * when someone actually chose it.
 *
 * UNITS: the loads columns have names that lie - length_cm / width_cm / height_cm
 * hold FEET and weight_kg holds POUNDS (the hub writes them that way; see the
 * hub's app.py note "four loads columns have names that lie"). That is exactly
 * what the hub's resolver expects (lb + ft), so no conversion happens here.
 */

import { supabase } from '../supabaseClient';
import { getRoutingConfig } from './routingEngineService';
import { cargoStopMinutes, loadCargoVocab, cargoVocabNow } from './hubCargo';   // __CARGO_V2__

const MIN_SAMPLES = 3;          // below this the learned value is not trusted
const CACHE_TTL_MS = 5 * 60000;
const LEGACY_PRESET_DEFAULT = 30;

// __STOP_TIME_TABLE__ (2026-09-29) Henry's per-stop numbers.
// TWIN of Billing-Manager/Formulas.py _STOP_SIZE_TIERS / _STOP_HEAVY_LB /
// _STOP_HEAVY_MIN / _DURATION_XLARGE (compute_per_stop_time layers 2-4). These
// are only the FALLBACK defaults: the live numbers are the hub settings
// stop_time_size_tiers / stop_time_heavy_lb / stop_time_heavy_min (Settings ->
// Job Duration), read through public.stop_time_settings(). Keep the defaults
// identical - sim/dwellplan.test.mjs fails if the two drift.
// (max_width_ft, max_length_ft, minutes): width = the shorter of L/W, length the
// longer; inclusive upper bounds, first fit wins.
export const STOP_SIZE_TIERS = [
  [4, 4, 7],
  [4, 16, 12],
  [8, 16, 15],
  [8, 24, 20],
  [8.5, 40, 25],
];
export const STOP_HEAVY_LB = 15000;   // over this = heavy equipment, whatever the size
export const STOP_HEAVY_MIN = 25;
export const DURATION_XLARGE = 25;    // bigger than every tier: max(this, default per stop)

let cache = { at: 0, byKey: new Map(), byType: new Map() };
let stopTimes = { at: 0, value: null };

/** Same normalisation as the SQL dwell_key(): lower, first comma part, alnum + single spaces. */
export const dwellKey = (address) => {
  if (!address) return null;
  const k = String(address).split(',')[0].trim().toLowerCase()
    .replace(/[^a-z0-9 ]/g, '').replace(/\s+/g, ' ');
  return k || null;
};

export const stopKind = (stop) => {
  if (!stop) return 'customer';
  if (stop.stop_type === 'fuel') return 'fuel_station';
  if (stop.stop_type === 'pickup') return 'yard';
  return 'customer';
};

export const loadDwellSettings = async (force = false) => {
  if (!force && Date.now() - cache.at < CACHE_TTL_MS && (cache.byKey.size || cache.byType.size)) return cache;
  try {
    const { data, error } = await supabase
      .from('dwell_time_settings')
      .select('id, location_name, location_address, location_key, location_type, default_dwell_minutes, ai_adjusted_dwell_minutes, sample_count, min_dwell_minutes, max_dwell_minutes, is_active')
      .eq('is_active', true);
    if (error) throw error;
    const byKey = new Map(), byType = new Map();
    for (const s of (data || [])) {
      if (s.location_key) byKey.set(s.location_key, s);
      else if (s.location_type && !byType.has(s.location_type)) byType.set(s.location_type, s);
    }
    cache = { at: Date.now(), byKey, byType };
  } catch (e) {
    console.error('loadDwellSettings error:', e);
    cache = { at: Date.now(), byKey: cache.byKey, byType: cache.byType };
  }
  return cache;
};

const toInt = (v) => {
  const n = parseInt(v, 10);
  return Number.isFinite(n) ? n : null;
};

const posNum = (v) => {
  const f = parseFloat(v);
  return Number.isFinite(f) && f > 0 ? f : null;
};

/** Same rules as Formulas.stop_time_size_config: a malformed / empty list = the defaults. */
export const parseSizeTiers = (raw) => {
  let v = raw;
  if (typeof v === 'string') {
    try { v = v.trim() ? JSON.parse(v) : null; } catch (e) { v = null; }
  }
  if (!Array.isArray(v) || !v.length) return STOP_SIZE_TIERS.map((r) => r.slice());
  const rows = [];
  for (const r of v) {
    if (!Array.isArray(r) || r.length !== 3) return STOP_SIZE_TIERS.map((x) => x.slice());
    const w = posNum(r[0]); const l = posNum(r[1]); const m = parseFloat(r[2]);
    if (w == null || l == null || !Number.isFinite(m) || m < 0) return STOP_SIZE_TIERS.map((x) => x.slice());
    rows.push([w, l, Math.round(m)]);
  }
  return rows;
};

/**
 * Normalise the hub's stop-time settings. cargo_type_stop_times is stored as a
 * JSON STRING of {cargo_type: minutes} (a dict is tolerated too) - same as
 * Formulas.compute_per_stop_time. stop_time_size_tiers the same way (a JSON
 * string of [max_width_ft, max_length_ft, minutes] rows).
 */
export const parseStopTimeSettings = (raw) => {
  const r = raw || {};
  let ct = r.cargo_type_stop_times;
  if (typeof ct === 'string') {
    try { ct = ct.trim() ? JSON.parse(ct) : {}; } catch (e) { ct = {}; }
  }
  if (!ct || typeof ct !== 'object' || Array.isArray(ct)) ct = {};
  const cargoTypeStopTimes = {};
  Object.keys(ct).forEach((k) => { const m = toInt(ct[k]); if (m != null) cargoTypeStopTimes[k] = m; });
  const dps = toInt(r.default_per_stop_min);
  const prep = toInt(r.start_drive_prep_min != null ? r.start_drive_prep_min : r.duration_start_prep_min);
  const heavyLb = posNum(r.stop_time_heavy_lb);
  const heavyMin = parseFloat(r.stop_time_heavy_min);
  return {
    cargoTypeStopTimes,
    defaultPerStopMin: dps != null ? dps : 15,   // Formulas.py default
    startPrepMin: prep != null ? Math.max(0, prep) : 10, // Formulas.py default
    sizeTiers: parseSizeTiers(r.stop_time_size_tiers),
    heavyLb: heavyLb != null && Number.isFinite(heavyLb) ? heavyLb : STOP_HEAVY_LB,
    heavyMin: Number.isFinite(heavyMin) && heavyMin >= 0 ? Math.round(heavyMin) : STOP_HEAVY_MIN,
  };
};

/**
 * The hub's global stop-time settings (cargo-type minutes, default per stop,
 * start prep). Read through public.stop_time_settings() - a SECURITY DEFINER
 * function that returns ONLY these keys; app_settings itself also holds the
 * Zoho token and is never selected from the board.
 */
export const loadStopTimeSettings = async (force = false) => {
  if (!force && stopTimes.value && Date.now() - stopTimes.at < CACHE_TTL_MS) return stopTimes.value;
  try {
    const { data, error } = await supabase.rpc('stop_time_settings');
    if (error) throw error;
    stopTimes = { at: Date.now(), value: parseStopTimeSettings(data) };
    // __CARGO_V2__ the hub's cargo list, so a job counts under its type (any of its names) and its category default
    try { stopTimes.value.cargoVocab = (await loadCargoVocab()) || null; } catch (e) { stopTimes.value.cargoVocab = cargoVocabNow(); }
  } catch (e) {
    console.warn('stop_time_settings unavailable, using defaults:', e);
    stopTimes = { at: Date.now(), value: stopTimes.value || parseStopTimeSettings(null) };
  }
  return stopTimes.value;
};

export const invalidateDwellCache = () => {
  cache = { at: 0, byKey: new Map(), byType: new Map() };
  stopTimes = { at: 0, value: null };
};

const valueOf = (setting) => {
  if (!setting) return null;
  if (setting.ai_adjusted_dwell_minutes && (setting.sample_count || 0) >= MIN_SAMPLES) {
    return setting.ai_adjusted_dwell_minutes;
  }
  return setting.default_dwell_minutes || null;
};

const num = (v) => {
  const f = parseFloat(v);
  return Number.isFinite(f) && f > 0 ? f : 0;
};

/**
 * The cargo on a stop: the routing engine copies it from the load onto each stop
 * (cargo_type / weight_lb / length_ft / width_ft / height_ft); a raw stop that
 * still carries its load (stop.load or stop.loads) works too.
 */
export const cargoOf = (stop) => {
  const s = stop || {};
  const l = s.load || s.loads || {};
  const pick = (a, b) => (a != null && a !== '' ? a : b);
  const ct = pick(s.cargo_type, pick(s.commodity, l.commodity));
  return {
    cargoType: ct != null && String(ct) !== '' ? String(ct) : null,
    weightLb: num(pick(s.weight_lb, l.weight_kg)),        // loads.weight_kg holds POUNDS
    lengthFt: num(pick(s.length_ft, l.length_cm)),        // loads.*_cm hold FEET
    widthFt: num(pick(s.width_ft, l.width_cm)),
    heightFt: num(pick(s.height_ft, l.height_cm)),
  };
};

/**
 * Formulas.stop_time_size_minutes for a cargo profile: over the heavy weight ->
 * heavy minutes; else the first footprint tier (width = shorter of L/W, length =
 * the longer) the load fits in; else max(25, default per stop). `st` is the
 * parseStopTimeSettings shape (a bare number is read as defaultPerStopMin, for
 * old callers). Returns null when the load has no weight and no dimension at
 * all: the hub would call that "the smallest tier", which for a job nobody
 * measured is a guess, not a fact - so the plan falls through to the place /
 * type defaults instead.
 */
export const sizeTierMinutes = (cargo, st = {}) => {
  const s = (st != null && typeof st === 'object') ? st : { defaultPerStopMin: st };
  const tiers = Array.isArray(s.sizeTiers) && s.sizeTiers.length ? s.sizeTiers : STOP_SIZE_TIERS;
  const heavyLb = s.heavyLb != null ? s.heavyLb : STOP_HEAVY_LB;
  const heavyMin = s.heavyMin != null ? s.heavyMin : STOP_HEAVY_MIN;
  const c = cargo || {};
  const w = num(c.weightLb);
  const l = num(c.lengthFt); const wd = num(c.widthFt);
  if (w <= 0 && Math.max(l, wd, num(c.heightFt)) <= 0) return null;
  if (w > heavyLb) return { minutes: heavyMin, tier: `heavy equipment (over ${heavyLb} lb)` };
  const short = Math.min(l, wd); const long = Math.max(l, wd);
  for (const [maxW, maxL, mins] of tiers) {
    if (short <= maxW && long <= maxL) return { minutes: mins, tier: `up to ${maxW} x ${maxL} ft` };
  }
  const d = toInt(s.defaultPerStopMin);
  return { minutes: Math.max(DURATION_XLARGE, d != null ? d : 15), tier: 'over the largest size' };
};

const reasonText = (r) => {
  switch (r.source) {
    case 'stop_override': return 'set on this stop';
    case 'learned': return `learned at this place from ${r.samples} real visits`;
    case 'cargo_type': return `cargo type "${r.cargoType}" (Job Duration settings)`;
    case 'size_tier': return `size tier ${r.tier}`;
    case 'location': return 'this place\'s default';
    case 'type_default': return 'default for this kind of stop';
    default: return 'routing default';
  }
};

/**
 * Pure resolution (no I/O) - the order in the header. `ctx` = { byKey, byType,
 * stopTimes (parseStopTimeSettings shape), fallback }.
 * @returns {{minutes:number, source:'stop_override'|'learned'|'cargo_type'|'size_tier'|'location'|'type_default'|'config', reason:string, setting?:object, samples?:number, cargoType?:string, tier?:string}}
 */
export const resolveStopDwellWith = (stop, ctx = {}) => {
  const fallback = ctx.fallback || 30;
  const done = (r) => ({ ...r, reason: reasonText(r) });
  if (!stop) return done({ minutes: fallback, source: 'config' });

  if (stop.dwell_minutes != null) return done({ minutes: stop.dwell_minutes, source: 'stop_override' });
  if (stop.preset_dwell_minutes != null && stop.preset_dwell_minutes !== LEGACY_PRESET_DEFAULT) {
    return done({ minutes: stop.preset_dwell_minutes, source: 'stop_override' });
  }

  const byKey = ctx.byKey || new Map();
  const byType = ctx.byType || new Map();
  const key = dwellKey(stop.address);
  const hit = key ? byKey.get(key) : null;
  if (hit && hit.ai_adjusted_dwell_minutes && (hit.sample_count || 0) >= MIN_SAMPLES) {
    return done({ minutes: hit.ai_adjusted_dwell_minutes, source: 'learned', setting: hit, samples: hit.sample_count || 0 });
  }

  const st = ctx.stopTimes || parseStopTimeSettings(null);
  const cargo = cargoOf(stop);
  if (cargo.cargoType && Object.prototype.hasOwnProperty.call(st.cargoTypeStopTimes, cargo.cargoType)) {
    return done({ minutes: st.cargoTypeStopTimes[cargo.cargoType], source: 'cargo_type', cargoType: cargo.cargoType });
  }
  // __CARGO_V2__ the type's entry under any of its names, else its category's default ('@<category>')
  const cm = cargo.cargoType ? cargoStopMinutes(st.cargoTypeStopTimes, cargo.cargoType, st.cargoVocab || null) : null;
  if (cm != null) return done({ minutes: cm, source: 'cargo_type', cargoType: cargo.cargoType });
  const tier = sizeTierMinutes(cargo, st);
  if (tier) return done({ minutes: tier.minutes, source: 'size_tier', tier: tier.tier });

  if (hit && hit.default_dwell_minutes) {
    return done({ minutes: hit.default_dwell_minutes, source: 'location', setting: hit, samples: hit.sample_count || 0 });
  }
  const typeHit = byType.get(stopKind(stop));
  const typeMinutes = valueOf(typeHit);
  if (typeMinutes) return done({ minutes: typeMinutes, source: 'type_default', setting: typeHit });

  return done({ minutes: fallback, source: 'config' });
};

/**
 * Minutes to allow at this stop, with the reason (for tooltips / the planner UI).
 * Same shape as resolveStopDwellWith.
 */
export const resolveStopDwell = async (stop) => {
  const config = getRoutingConfig();
  const fallback = config.defaultDwellMinutes || 30;
  if (!stop) return resolveStopDwellWith(null, { fallback });
  const { byKey, byType } = await loadDwellSettings();
  const st = await loadStopTimeSettings();
  return resolveStopDwellWith(stop, { byKey, byType, stopTimes: st, fallback });
};

/** Convenience for callers that only want the number. */
export const dwellMinutesForStop = async (stop) => (await resolveStopDwell(stop)).minutes;

/** Address-only resolution (quoting, before a load_stops row exists). */
export const dwellMinutesForAddress = async (address, stopType = 'delivery') =>
  dwellMinutesForStop({ address, stop_type: stopType });

export default { dwellKey, stopKind, loadDwellSettings, loadStopTimeSettings, parseStopTimeSettings, invalidateDwellCache, resolveStopDwell, resolveStopDwellWith, sizeTierMinutes, parseSizeTiers, cargoOf, dwellMinutesForStop, dwellMinutesForAddress, STOP_SIZE_TIERS, STOP_HEAVY_LB, STOP_HEAVY_MIN, DURATION_XLARGE };
